import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useDebug } from '../contexts/DebugContext';
import { DashboardPanelPicker } from '../components/DashboardView';
import { can } from '../utils/permissions';
import { IconButton, Banner } from '../components/ui';

// Settings (gear icon): /settings = My Settings, /settings/admin = Admin tools.
// initialSyncResult is the app's start-up job/period sync (App.js), shown here
// until a manual Refresh Jobs replaces it.
export default function SettingsPage({ initialSyncResult }) {
  const navigate = useNavigate();
  const { tab } = useParams();
  const { user, startEmulation, refreshUser } = useAuth();
  const { debugMode, setDebugMode } = useDebug();
  const isAdmin = can(user, 'admin');
  const isEmulating = !!user?.emulating;
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [resources, setResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [resourcesUpdating, setResourcesUpdating] = useState(false);
  const [emulateSearch, setEmulateSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(initialSyncResult || null);
  useEffect(() => { if (initialSyncResult) setSyncResult(initialSyncResult); }, [initialSyncResult]);
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [showManageUsers, setShowManageUsers] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [togglingUserId, setTogglingUserId] = useState(null);
  const [showEmailMap, setShowEmailMap] = useState(false);
  const [emailMappings, setEmailMappings] = useState([]);
  const [unmappedEmails, setUnmappedEmails] = useState([]);
  const [mapExt, setMapExt] = useState('');
  const [mapRcp, setMapRcp] = useState('');
  const [mapBusy, setMapBusy] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [permEmail, setPermEmail] = useState('');
  const [permResult, setPermResult] = useState(null);
  const [permBusy, setPermBusy] = useState(false);
  const [permError, setPermError] = useState(null);
  const [refreshMsg, setRefreshMsg] = useState(null);
  // Global (universal) budget setting — admin only.
  const [budgetHideNonJobs, setBudgetHideNonJobs] = useState(null);
  const [budgetSettingBusy, setBudgetSettingBusy] = useState(false);


  // Load the global budget setting for admins (once).
  useEffect(() => {
    if (!isAdmin || isEmulating) return;
    let cancelled = false;
    api.getBudgetSettings()
      .then((r) => { if (!cancelled) setBudgetHideNonJobs(!!r.hideNonJobsAndGoal); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin, isEmulating]);

  const toggleBudgetHideNonJobs = async (checked) => {
    setBudgetHideNonJobs(checked); // optimistic
    setBudgetSettingBusy(true);
    try {
      await api.setBudgetHideNonJobsAndGoal(checked);
    } catch (e) {
      setBudgetHideNonJobs(!checked); // revert
      alert(e.message || 'Failed to save budget setting');
    } finally {
      setBudgetSettingBusy(false);
    }
  };

  // Per-user Dashboard panels, in display order (none = no Dashboard tab).
  const [savingPanels, setSavingPanels] = useState(false);
  const saveDashboardPanels = async (next) => {
    setSavingPanels(true);
    try {
      await api.setPreferences({ dashboardPanels: next });
      await refreshUser();
    } catch (e) {
      alert(e.message || 'Failed to save dashboard panels');
    } finally {
      setSavingPanels(false);
    }
  };

  // Per-user "Show Next/Previous Expense" preference.
  const [savingExpenseNav, setSavingExpenseNav] = useState(false);
  const toggleShowExpenseNav = async (checked) => {
    setSavingExpenseNav(true);
    try {
      await api.setPreferences({ showExpenseNav: checked });
      await refreshUser();
    } catch (e) {
      alert(e.message || 'Failed to save preference');
    } finally {
      setSavingExpenseNav(false);
    }
  };

  const handleRefresh = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      setSyncResult(await api.refreshSync());
    } catch (e) {
      setSyncResult({ errors: [e.message], log: [] });
    } finally {
      setSyncing(false);
    }
  };

  const dismissSyncResult = () => setSyncResult(null);

  if (tab && tab !== 'admin') return <Navigate to="/settings" replace />;
  if (tab === 'admin' && !isAdmin) return <Navigate to="/settings" replace />;

  return (
    <>
      <div style={{ padding: '16px' }}>
        {tab === 'admin' ? (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Admin</span>
            </div>
            {isAdmin && !isEmulating && (
              <div style={{ padding: '12px 0', borderTop: '1px solid var(--gray-200)' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={invoiceNo}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 20);
                      setInvoiceNo(v);
                      setInvoiceError(null);
                    }}
                    placeholder="Sales Invoice No."
                    style={{
                      padding: '6px 10px', border: '1px solid var(--gray-300)',
                      borderRadius: 'var(--radius)', fontSize: '14px', width: '180px',
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && invoiceNo.trim()) document.getElementById('btn-invoice-pdf').click(); }}
                  />
                  <button
                    id="btn-invoice-pdf"
                    className="btn btn-outline"
                    disabled={!invoiceNo.trim() || invoiceLoading}
                    onClick={async () => {
                      setInvoiceLoading(true);
                      setInvoiceError(null);
                      try {
                        const url = api.getInvoiceReceiptsPdfUrl(invoiceNo.trim());
                        const resp = await fetch(url, { credentials: 'same-origin' });
                        if (!resp.ok) {
                          const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
                          throw new Error(err.error || `HTTP ${resp.status}`);
                        }
                        const blob = await resp.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        window.open(blobUrl, '_blank');
                      } catch (e) {
                        setInvoiceError(e.message);
                      } finally {
                        setInvoiceLoading(false);
                      }
                    }}
                  >
                    {invoiceLoading ? 'Generating...' : 'Get Receipts for Invoice'}
                  </button>
                </div>
                {invoiceError && (
                  <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>{invoiceError}</div>
                )}
                <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
                  Generate a PDF of receipts from SharePoint for a posted sales invoice
                </div>
              </div>
            )}
            {isAdmin && !isEmulating && (
              <div style={{ padding: '12px 0', borderTop: '1px solid var(--gray-200)' }}>
                <button className="btn btn-outline" onClick={async () => {
                  setShowUserPicker(true);
                  setResources([]);
                  setLoadingResources(true);
                  setResourcesUpdating(false);
                  let shownLocal = false;
                  // 1) Show the local Azure SQL user list right away (fast) so the
                  //    admin doesn't wait on Navision.
                  try {
                    const local = await api.getResourcesLocal();
                    if (Array.isArray(local) && local.length) {
                      setResources(local);
                      setLoadingResources(false);
                      setResourcesUpdating(true);
                      shownLocal = true;
                    }
                  } catch (_) { /* fall through to the live list */ }
                  // 2) Replace with the authoritative Navision resource list (plus
                  //    non-@rcp.com app users) when it comes back.
                  try {
                    const [res, appUsers] = await Promise.all([
                      api.getResources(),
                      api.getAppUsers().catch(() => []),
                    ]);
                    const appEntries = (appUsers || []).map((u) => ({
                      No: null, Name: u.DisplayName || u.Email, User_ID: u.Email, appUserId: u.UserID, isAppUser: true,
                    }));
                    setResources([...(res || []), ...appEntries]);
                  } catch (e) {
                    // Keep the local list if we already showed one; otherwise fail.
                    if (!shownLocal) {
                      alert(e.message || 'Failed to load resources');
                      setShowUserPicker(false);
                    }
                  } finally {
                    setLoadingResources(false);
                    setResourcesUpdating(false);
                  }
                }}>
                  Emulate User
                </button>
                <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
                  View and manage timesheets as another user (admin only)
                </div>
              </div>
            )}
            {isAdmin && !isEmulating && (
              <div style={{ padding: '12px 0', borderTop: '1px solid var(--gray-200)' }}>
                <button className="btn btn-outline" onClick={async () => {
                  setShowManageUsers(true);
                  setLoadingUsers(true);
                  setUserSearch('');
                  try {
                    const users = await api.getUsers();
                    setAllUsers(users);
                  } catch (e) {
                    alert(e.message || 'Failed to load users');
                    setShowManageUsers(false);
                  } finally {
                    setLoadingUsers(false);
                  }
                }}>
                  Manage Users
                </button>
                <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
                  View all users and enable or disable accounts (admin only)
                </div>
              </div>
            )}
            {isAdmin && !isEmulating && (
              <div style={{ padding: '12px 0', borderTop: '1px solid var(--gray-200)' }}>
                <button className="btn btn-outline" onClick={async () => {
                  setShowEmailMap(true);
                  setMapError(null); setMapExt(''); setMapRcp('');
                  try { const r = await api.getEmailMappings(); setEmailMappings(r.mappings || []); setUnmappedEmails(r.unmapped || []); }
                  catch (e) { alert(e.message || 'Failed to load email mappings'); setShowEmailMap(false); }
                }}>
                  Map External Emails
                </button>
                <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
                  Map a non-@rcp.com login email to an @rcp.com email (admin only)
                </div>
              </div>
            )}
            {isAdmin && !isEmulating && (
              <div style={{ padding: '12px 0', borderTop: '1px solid var(--gray-200)' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px' }}>Budget Settings</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: budgetHideNonJobs == null ? 'default' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!budgetHideNonJobs}
                    disabled={budgetHideNonJobs == null || budgetSettingBusy}
                    onChange={(e) => toggleBudgetHideNonJobs(e.target.checked)}
                  />
                  Hide Non-Jobs and Goal in Budget
                </label>
                <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
                  Applies to everyone: on the Budget and Budget Admin screens, hides the Opportunities and Other sections and the Quarter Goal panel (admin only).
                </div>
              </div>
            )}
            {isAdmin && (
              <div style={{ padding: '12px 0', borderTop: '1px solid var(--gray-200)' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px' }}>Navision Permissions</div>
                <button
                  className="btn btn-outline"
                  disabled={permBusy}
                  onClick={async () => {
                    setPermBusy(true);
                    setPermError(null);
                    setRefreshMsg(null);
                    try {
                      const r = await api.refreshPermissions();
                      await refreshUser();
                      if (r.found) {
                        setRefreshMsg(`Refreshed ${r.emulated ? `${r.displayName || r.email} ` : ''}from Navision: Navision Web Access = ${r.navisionWebAccess ? 'YES' : 'no'}, Budget Access = ${r.budgetAccess ? 'YES' : 'no'}, Budget Admin = ${r.budgetAdmin ? 'YES' : 'no'}, Show On Director List = ${r.showOnDirectorList ? 'YES' : 'no'}, Accounting Access = ${r.accountingAccess ? 'YES' : 'no'}, Executive Access = ${r.executiveAccess ? 'YES' : 'no'}, Admin = ${r.isAdmin ? 'YES' : 'no'}.`);
                      } else {
                        setRefreshMsg(r.message || 'No Navision Resource record found.');
                      }
                    } catch (e) {
                      setPermError(e.message || 'Failed to refresh permissions');
                    } finally {
                      setPermBusy(false);
                    }
                  }}
                >
                  {permBusy ? 'Working…' : isEmulating
                    ? `Refresh ${user.emulating.displayName || 'Emulated User'}'s Permissions from Navision`
                    : 'Refresh My Permissions from Navision'}
                </button>
                <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
                  {isEmulating ? 'Re-reads the emulated user’s Navision Resource record and updates the emulation.' : 'Re-reads your Navision Resource record and updates your session — no need to log out and back in.'}
                </div>
                <Banner kind="ok" onClose={() => setRefreshMsg(null)}>{refreshMsg}</Banner>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '12px' }}>
                  <input
                    className="form-control"
                    type="email"
                    placeholder={user?.email || 'email@rcp.com'}
                    value={permEmail}
                    onChange={(e) => setPermEmail(e.target.value)}
                    style={{ width: 'auto', minWidth: '220px', fontSize: '13px' }}
                  />
                  <button
                    className="btn btn-outline"
                    disabled={permBusy}
                    onClick={async () => {
                      setPermBusy(true);
                      setPermError(null);
                      setPermResult(null);
                      try {
                        const r = await api.getNavResource((permEmail || '').trim() || undefined);
                        setPermResult(r);
                      } catch (e) {
                        setPermError(e.message || 'Failed to query Navision');
                      } finally {
                        setPermBusy(false);
                      }
                    }}
                  >
                    Check Navision Permissions
                  </button>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
                  Shows the raw Navision Resource record and resolved flags (defaults to your email).
                </div>
                <Banner kind="err" onClose={() => setPermError(null)}>{permError}</Banner>
                {permResult && (
                  <div style={{ marginTop: '8px', fontSize: '12px' }}>
                    <div style={{ marginBottom: '6px' }}>
                      <strong>{permResult.email}</strong> → NAV User ID <code>{permResult.navUserId}</code> · records found: <strong>{permResult.recordCount}</strong>
                    </div>
                    {permResult.resolvedPermissions && !permResult.resolvedPermissions.error && (
                      <div style={{ marginBottom: '6px' }}>
                        Resolved: Navision Web Access = <strong>{permResult.resolvedPermissions.navisionWebAccess ? 'YES' : 'no'}</strong>,
                        {' '}Budget Access = <strong>{permResult.resolvedPermissions.budgetAccess ? 'YES' : 'no'}</strong>,
                        {' '}Budget Admin = <strong>{permResult.resolvedPermissions.budgetAdmin ? 'YES' : 'no'}</strong>,
                        {' '}Show On Director List = <strong>{permResult.resolvedPermissions.showOnDirectorList ? 'YES' : 'no'}</strong>,
                        {' '}Accounting Access = <strong>{permResult.resolvedPermissions.accountingAccess ? 'YES' : 'no'}</strong>,
                        {' '}Executive Access = <strong>{permResult.resolvedPermissions.executiveAccess ? 'YES' : 'no'}</strong>,
                        {' '}Admin = <strong>{permResult.resolvedPermissions.isAdmin ? 'YES' : 'no'}</strong>
                      </div>
                    )}
                    <pre style={{ background: 'var(--gray-100, #f4f4f5)', border: '1px solid var(--gray-200)', borderRadius: '6px', padding: '10px', overflowX: 'auto', maxHeight: '320px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(permResult.rawRecord, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
          <div className="card">
            <div className="card-header">
              <span className="card-title">My Settings</span>
            </div>
            <div style={{ padding: '12px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={debugMode}
                  onChange={(e) => setDebugMode(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>Debug Mode</span>
              </label>
              <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px', paddingLeft: '28px' }}>
                Show detailed API call and submission debug information
              </div>
            </div>
            <div style={{ padding: '12px 0', borderTop: '1px solid var(--gray-200)' }}>
              <div style={{ fontSize: '14px', marginBottom: '6px' }}>Dashboard panels</div>
              <DashboardPanelPicker user={user} saving={savingPanels} onChange={saveDashboardPanels} />
            </div>
            <div style={{ padding: '12px 0', borderTop: '1px solid var(--gray-200)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: savingExpenseNav ? 'default' : 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={!!user?.showExpenseNav}
                  disabled={savingExpenseNav}
                  onChange={(e) => toggleShowExpenseNav(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>Show Next/Previous Expense</span>
              </label>
              <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px', paddingLeft: '28px' }}>
                Show the Next/Previous buttons on the expense entry screen to move between expenses
              </div>
            </div>
            <div style={{ padding: '12px 0', borderTop: '1px solid var(--gray-200)' }}>
              <button
                className="btn btn-outline"
                onClick={handleRefresh}
                disabled={syncing}
              >
                {syncing ? 'Syncing...' : 'Refresh Jobs'}
              </button>
              <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
                Sync jobs and time periods from NAV
              </div>
            </div>
          </div>
          {syncResult && (
            <div className="card" style={{ position: 'relative', marginTop: '12px' }}>
              <IconButton icon="close" title="Close" onClick={dismissSyncResult}
                style={{ position: 'absolute', top: '8px', right: '12px' }} />
              <div className="card-header">
                <span className="card-title">
                  {syncResult.errors?.length > 0 ? 'Sync Completed with Errors' : 'Sync Complete'}
                </span>
              </div>
              {syncResult.errors?.length > 0 && (
                <Banner kind="err" style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {syncResult.errors.map((err, i) => (
                    <div key={i}>{err}</div>
                  ))}
                </Banner>
              )}
            </div>
          )}
          </>
        )}
      </div>

      {/* User Picker Modal for Emulation (from Advanced tab) */}
      {showUserPicker && (
        <div className="modal-overlay" onClick={() => setShowUserPicker(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <strong>Select User to Emulate</strong>
              <IconButton icon="close" title="Close" onClick={() => setShowUserPicker(false)} />
            </div>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or resource no..."
              value={emulateSearch}
              onChange={(e) => setEmulateSearch(e.target.value)}
              style={{ marginBottom: '12px' }}
              autoFocus
            />
            {resourcesUpdating && (
              <div style={{ fontSize: '11px', color: 'var(--gray-400)', marginBottom: '8px' }}>Updating from Navision…</div>
            )}
            {loadingResources ? (
              <div className="loading"><div className="spinner" />Loading resources...</div>
            ) : (() => {
              const q = emulateSearch.toLowerCase();
              const filtered = resources.filter((r) =>
                !q || r.Name?.toLowerCase().includes(q) || r.No?.toLowerCase().includes(q) || r.User_ID?.toLowerCase().includes(q)
              );
              return filtered.length === 0 ? (
                <div className="empty-state"><p>No matching resources.</p></div>
              ) : (
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                  {filtered.map((r) => (
                    <div
                      key={r.isAppUser ? `app-${r.appUserId}` : r.No}
                      className="ts-list-item"
                      onClick={async () => {
                        try {
                          await startEmulation(r.isAppUser ? null : r.No, r.Name, r.isAppUser ? r.appUserId : r.User_ID);
                          setShowUserPicker(false);
                          navigate('/');
                        } catch (e) {
                          alert(e.message || 'Failed to emulate user');
                        }
                      }}
                    >
                      <div>
                        <div className="ts-period">
                          {r.Name}
                          {r.isAppUser && <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 600, color: '#b45309', background: '#fef3c7', borderRadius: '4px', padding: '1px 5px' }}>non-RCP</span>}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>{r.isAppUser ? r.User_ID : r.No}</div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {showEmailMap && (
        <div className="modal-overlay" onClick={() => setShowEmailMap(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '620px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <strong>Map External Emails</strong>
              <IconButton icon="close" title="Close" onClick={() => setShowEmailMap(false)} />
            </div>
            <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginBottom: '12px' }}>
              Map a non-@rcp.com login email (e.g. john.smith@company.com) to an @rcp.com email
              (e.g. jsmith@rcp.com). The user can then sign in with either email and reach the same
              account; timesheet submissions to Navision use the @rcp.com email.
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Unmapped external users {unmappedEmails.length > 0 ? `(${unmappedEmails.length})` : ''}</label>
              <select
                className="form-control"
                value=""
                onChange={(e) => { if (e.target.value) setMapExt(e.target.value); }}
                disabled={unmappedEmails.length === 0}
              >
                <option value="">{unmappedEmails.length === 0 ? 'No unmapped external users' : 'Pick an external user to map…'}</option>
                {unmappedEmails.map((u) => (
                  <option key={u.Email} value={u.Email}>{u.DisplayName ? `${u.DisplayName} — ${u.Email}` : u.Email}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '10px' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={{ fontSize: '11px', color: 'var(--gray-500)' }}>External email (pick above or type)</label>
                <input className="form-control" type="email" placeholder="john.smith@company.com" value={mapExt} onChange={(e) => setMapExt(e.target.value)} />
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={{ fontSize: '11px', color: 'var(--gray-500)' }}>@rcp.com email</label>
                <input className="form-control" type="email" placeholder="jsmith@rcp.com" value={mapRcp} onChange={(e) => setMapRcp(e.target.value)} />
              </div>
              <button className="btn btn-primary" disabled={mapBusy} onClick={async () => {
                setMapBusy(true); setMapError(null);
                try {
                  await api.addEmailMapping(mapExt.trim(), mapRcp.trim());
                  const r = await api.getEmailMappings(); setEmailMappings(r.mappings || []); setUnmappedEmails(r.unmapped || []);
                  setMapExt(''); setMapRcp('');
                } catch (e) { setMapError(e.message || 'Failed to add mapping'); }
                finally { setMapBusy(false); }
              }}>Add</button>
            </div>
            {mapError && (
              <Banner kind="err" onClose={() => setMapError(null)} style={{ margin: '0 0 10px' }}>{mapError}</Banner>
            )}
            {emailMappings.length === 0 ? (
              <div className="empty-state"><p>No email mappings yet.</p></div>
            ) : (
              <div style={{ maxHeight: '320px', overflow: 'auto' }}>
                {emailMappings.map((m) => (
                  <div key={m.ExternalEmail} className="ts-list-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '13px' }}>{m.ExternalEmail}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>→ {m.RcpEmail}</div>
                    </div>
                    <button className="btn btn-sm btn-danger" disabled={mapBusy} onClick={async () => {
                      if (!window.confirm(`Remove the mapping for ${m.ExternalEmail}?`)) return;
                      setMapBusy(true); setMapError(null);
                      try { await api.deleteEmailMapping(m.ExternalEmail); const r = await api.getEmailMappings(); setEmailMappings(r.mappings || []); setUnmappedEmails(r.unmapped || []); }
                      catch (e) { setMapError(e.message || 'Failed to remove mapping'); }
                      finally { setMapBusy(false); }
                    }} style={{ fontSize: '11px' }}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showManageUsers && (
        <div className="modal-overlay" onClick={() => setShowManageUsers(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <strong>Manage Users</strong>
              <IconButton icon="close" title="Close" onClick={() => setShowManageUsers(false)} />
            </div>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              style={{ marginBottom: '12px' }}
              autoFocus
            />
            {loadingUsers ? (
              <div className="loading"><div className="spinner" />Loading users...</div>
            ) : (() => {
              const q = userSearch.toLowerCase();
              const filtered = allUsers.filter((u) =>
                !q || u.DisplayName?.toLowerCase().includes(q) || u.Email?.toLowerCase().includes(q)
              );
              return filtered.length === 0 ? (
                <div className="empty-state"><p>No matching users.</p></div>
              ) : (
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                  {filtered.map((u) => (
                    <div
                      key={u.UserID}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 12px', borderBottom: '1px solid var(--gray-100)',
                        opacity: u.IsActive ? 1 : 0.55,
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                          {u.DisplayName}
                          {!u.IsActive && (
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>DISABLED</span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
                          {u.Email}
                          {!u.MicrosoftOID && <span style={{ marginLeft: '6px', fontStyle: 'italic' }}>(never logged in)</span>}
                        </div>
                      </div>
                      <label
                        title="Hide the # prefix on invoice numbers for this user"
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--gray-500)', flexShrink: 0, marginLeft: '8px', cursor: 'pointer', userSelect: 'none' }}
                      >
                        <input
                          type="checkbox"
                          checked={!!u.HideInvoiceNumberPrefix}
                          onChange={async (e) => {
                            const hide = e.target.checked;
                            setAllUsers(prev => prev.map(x => x.UserID === u.UserID ? { ...x, HideInvoiceNumberPrefix: hide } : x));
                            try {
                              await api.setUserInvoicePrefix(u.UserID, hide);
                            } catch (err) {
                              setAllUsers(prev => prev.map(x => x.UserID === u.UserID ? { ...x, HideInvoiceNumberPrefix: !hide } : x));
                              alert(err.message || 'Failed to update preference');
                            }
                          }}
                        />
                        Hide #
                      </label>
                      <button
                        className={`btn btn-sm ${u.IsActive ? 'btn-danger' : 'btn-primary'}`}
                        style={{ fontSize: '11px', padding: '4px 10px', flexShrink: 0, marginLeft: '8px' }}
                        disabled={togglingUserId === u.UserID || u.UserID === user?.userId}
                        onClick={async () => {
                          const action = u.IsActive ? 'disable' : 'enable';
                          if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${u.DisplayName} (${u.Email})?`)) return;
                          setTogglingUserId(u.UserID);
                          try {
                            const result = await api.toggleUserActive(u.UserID);
                            setAllUsers(prev => prev.map(x => x.UserID === u.UserID ? { ...x, IsActive: result.isActive } : x));
                          } catch (e) {
                            alert(e.message || 'Failed to update user');
                          } finally {
                            setTogglingUserId(null);
                          }
                        }}
                      >
                        {togglingUserId === u.UserID ? '...' : u.IsActive ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div style={{ fontSize: '11px', color: 'var(--gray-400)', marginTop: '8px' }}>
              {allUsers.length} total user{allUsers.length !== 1 ? 's' : ''}
              {' '}&middot; {allUsers.filter(u => !u.IsActive).length} disabled
            </div>
          </div>
        </div>
      )}
    </>
  );
}
